import { useEffect, useState } from 'react';
import axiosInstance from '../api/axiosInstance';
import EmojiPicker from 'emoji-picker-react';

const predefinedAvatars = [
  '/avatars/avatar1.png',
  '/avatars/avatar2.png',
  '/avatars/avatar3.png',
  '/avatars/avatar4.png',
  // Weitere vorgegebene Avatare
];

export default function UserCP() {
  const [user, setUser] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [signature, setSignature] = useState('');
  const [loading, setLoading] = useState(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    axiosInstance.get('/usercp/me').then(res => {
      setUser(res.data);
      setSignature(res.data.signatureBBCode || '');
      setLoading(false);
    });
  }, []);

  const reloadUser = async () => {
    const res = await axiosInstance.get('/usercp/me');
    setUser(res.data);
  };

  const handleAvatarUpload = async (e) => {
    e.preventDefault();
    if (!avatarFile) return;

    const formData = new FormData();
    formData.append('avatar', avatarFile);

    await axiosInstance.post('/usercp/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    await reloadUser();
    setSuccessMsg('Avatar erfolgreich aktualisiert');
  };

  const handleAvatarChoice = async (url) => {
    await axiosInstance.patch('/usercp/avatar-choice', { avatarUrl: url });
    await reloadUser();
    setSuccessMsg('Avatar erfolgreich geändert');
  };

  const handleSignatureSave = async (e) => {
    e.preventDefault();
    await axiosInstance.patch('/usercp/signature', { signature });
    await reloadUser();
    setSuccessMsg('Signatur erfolgreich gespeichert');
  };

  const insertBBCode = (tag) => {
    setSignature(prev => `${prev}[${tag}][/${tag}]`);
  };

  const handleEmojiClick = (emojiData) => {
    setSignature(prev => prev + emojiData.emoji);
  };

  const getFullAvatarUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `https://forumapi.more-features.com${url}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="modern-card p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-green mx-auto"></div>
          <p className="text-center mt-4 text-gray-600">Lade Benutzerdaten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-green to-green-600 bg-clip-text text-transparent mb-2">
            Benutzerkontrollzentrum
          </h1>
          <p className="text-gray-600">Verwalte dein Profil und deine Einstellungen</p>
        </div>

        {successMsg && (
          <div className="modern-card mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mr-3">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-green-800 font-medium">{successMsg}</span>
              </div>
              <button 
                className="text-green-600 hover:text-green-800 transition-colors"
                onClick={() => setSuccessMsg('')}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Avatar-Bereich */}
          <div className="modern-card p-6">
            <div className="flex items-center mb-6">
              <div className="w-8 h-8 bg-gradient-to-r from-primary-green to-green-600 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Avatar verwalten</h3>
            </div>

            {/* Current Avatar Display */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <p className="font-medium text-gray-700 mb-4">Aktueller Avatar:</p>
              <div className="flex items-center justify-center">
                {user.avatarUrl ? (
                  <div className="relative">
                    <img
                      src={getFullAvatarUrl(user.avatarUrl)}
                      alt="Avatar"
                      className="w-32 h-32 object-cover rounded-full border-4 border-white shadow-lg"
                    />
                    <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                ) : (
                  <div className="w-32 h-32 bg-gray-200 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
                    <svg className="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
              {!user.avatarUrl && (
                <p className="text-center text-gray-500 italic mt-2">Kein Avatar gesetzt</p>
              )}
            </div>

            {/* Upload Form */}
            <form onSubmit={handleAvatarUpload} className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Eigenen Avatar hochladen
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setAvatarFile(e.target.files[0])}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-3 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-green file:text-white hover:file:bg-primary-green-dark transition-colors border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent"
                  />
                </div>
              </div>
              <button 
                type="submit" 
                disabled={!avatarFile}
                className="glass-btn text-white px-6 py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 w-full"
              >
                Avatar hochladen
              </button>
            </form>

            <div className="relative flex items-center justify-center my-6">
              <div className="border-t border-gray-300 absolute w-full"></div>
              <span className="bg-white px-4 text-gray-500 text-sm">oder</span>
            </div>

            {/* Predefined Avatars */}
            <div>
              <p className="font-medium text-gray-700 mb-4">Vorgegebenes Avatar wählen:</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {predefinedAvatars.map((url, idx) => (
                  <div key={idx} className="relative group cursor-pointer">
                    <img
                      src={getFullAvatarUrl(url)}
                      alt={`Avatar ${idx + 1}`}
                      className={`w-full aspect-square rounded-full object-cover transition-all duration-200 ${
                        user.avatarUrl === url
                          ? 'ring-4 ring-primary-green ring-offset-2 scale-105'
                          : 'ring-2 ring-gray-200 hover:ring-primary-green hover:scale-105 group-hover:shadow-lg'
                      }`}
                      onClick={() => handleAvatarChoice(url)}
                    />
                    {user.avatarUrl === url && (
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-primary-green rounded-full flex items-center justify-center border-2 border-white">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Signatur-Bereich */}
          <div className="modern-card p-6">
            <div className="flex items-center mb-6">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Signatur bearbeiten</h3>
            </div>

            <form onSubmit={handleSignatureSave} className="space-y-4">
              {/* BBCode Toolbar */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-3">BBCode-Werkzeuge:</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { tag: 'b', label: 'B', title: 'Fett' },
                    { tag: 'i', label: 'I', title: 'Kursiv' },
                    { tag: 'u', label: 'U', title: 'Unterstrichen' },
                    { tag: 'color=red', label: 'C', title: 'Farbe' },
                    { tag: 'size=20', label: 'S', title: 'Größe' },
                    { tag: 's', label: 'S̶', title: 'Durchgestrichen' }
                  ].map(({ tag, label, title }) => (
                    <button
                      key={tag}
                      type="button"
                      title={title}
                      className="px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-primary-green hover:text-white hover:border-primary-green transition-all duration-200 text-sm font-medium"
                      onClick={() => insertBBCode(tag)}
                    >
                      {label}
                    </button>
                  ))}
                  <button
                    type="button"
                    title="Emoji hinzufügen"
                    className="px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-yellow-400 hover:border-yellow-400 transition-all duration-200 text-sm"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    😊
                  </button>
                </div>
              </div>

              {/* Textarea */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Signatur-Text (BBCode unterstützt)
                </label>
                <textarea
                  rows={6}
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder="Erstelle deine persönliche Signatur..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent resize-none transition-all duration-200"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Verwende BBCode für Formatierung: [b]fett[/b], [i]kursiv[/i], [color=red]rot[/color]
                </p>
              </div>

              {/* Emoji Picker */}
              {showEmojiPicker && (
                <div className="relative">
                  <div className="absolute z-10 bg-white rounded-lg shadow-xl border border-gray-200 p-2">
                    <EmojiPicker onEmojiClick={handleEmojiClick} height={300} width={300} />
                  </div>
                </div>
              )}

              {/* Save Button */}
              <button 
                type="submit" 
                className="glass-btn text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 w-full"
              >
                Signatur speichern
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
